// Submission for the Overwolf uninstall survey (/ow-survey).
//
// Deliberately NOT routed through src/api/http.ts: that axios instance carries
// the app's interceptor stack (auth header, retry/recovery, error toasts, loader
// bar) and assumes a booted app. The survey page renders outside the App shell
// for a user whose app has just been deleted — no session, no stores, no toasts.
// A bare fetch is the whole dependency. (environment is a plain config object, not
// app-shell state, so importing it here keeps the page self-contained.)
//
// ENDPOINT: the backend's generic form-intake endpoint. The uninstall survey is
// one form type (formType = OVERWOLF_APP_DELETE) among others this endpoint takes.
// Built from environment.baseUrlWithApi so dev (proxied /mypart-webapp) and prod
// (absolute www.mypart.com) both resolve correctly.
import { environment } from '../config/environment';

export const OW_SURVEY_ENDPOINT = `${environment.baseUrlWithApi}auth/send-form`;

/** Discriminates this submission at the shared send-form endpoint. */
export const OW_SURVEY_FORM_TYPE = 'OVERWOLF_APP_DELETE';

/** Stable machine keys. Labels get reworded; these do not — the stored key is
 *  what analytics groups on, so never send display text alone. */
export const SURVEY_REASONS = [
  { key: 'reinstalling', label: 'I am just reinstalling, or will reinstall later' },
  { key: 'technical_issues', label: 'Technical issues (crashes, bugs, app failing to open)' },
  { key: 'performance', label: "It hurt my game's performance" },
  { key: 'not_useful', label: 'The music matching or search was not useful for me' },
  { key: 'confusing', label: 'The app was confusing or hard to use' },
  { key: 'missing_features', label: 'It is missing features I need' },
  { key: 'alternative', label: 'I found a better alternative' },
  { key: 'ads', label: 'The ads annoyed me' },
  { key: 'no_longer_needed', label: 'I no longer play with it or need it' },
  { key: 'overwolf', label: 'I like the app, I just do not want to use Overwolf' },
  { key: 'other', label: 'Other' },
] as const;

export type SurveyReasonKey = (typeof SURVEY_REASONS)[number]['key'];

export const COMMENT_MAX_LENGTH = 2000;

export interface SurveySubmission {
  /** Always OW_SURVEY_FORM_TYPE — routes the submission at the shared endpoint. */
  formType: typeof OW_SURVEY_FORM_TYPE;
  reason: SurveyReasonKey;
  reasonLabel: string;
  comment: string;
  /** Omitted entirely when the uninstaller sent no email — never sent empty. */
  email?: string;
  /** Only when the uninstaller appended a `userID` param; omitted otherwise. */
  userID?: string;
  appVersion: string;
  source: 'ow-uninstall';
}

export async function submitSurvey(payload: SurveySubmission): Promise<void> {
  const res = await fetch(OW_SURVEY_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Survey submission failed: ${res.status}`);
  }
}
